/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines */
import { select } from '@angular-redux/store';
import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  chain,
  concat,
  eq,
  every,
  filter,
  find,
  forEach,
  get,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  omit,
  pick,
  reduce,
  set,
  size,
  some,
  unset,
  keys,
  compact,
  isArray,
  gt,
  assignIn,
} from 'lodash';

import {
  CompanyUtilService,
  DataSource,
  DmDataService,
  SitesService,
  ValidationService,
  STATE_KEYS,
} from '@shared/services';
import {
  COMMON_ACTION_TYPES,
  CreateFormRules,
  ENTITY_TYPES,
  EntityId,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  IBulkUpdateDocumentsResult,
  IDocumentOperationOptions,
  IEvent,
  IFilterQuery,
  ILimit,
  IModalDialogOptions,
  ISite,
  ITransferData,
  ITransferDestination,
  ITransferGrandTotal,
  ITransferGroupFormFieldPath,
  NotificationType,
  REPLENISH_TYPE_NAMES,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_TYPE,
  ITask,
  TooltipIconConfig,
  ReallyChangedTransfers,
  ITaskOptions,
  IWidgetAction,
  EDIT_GRID_ITEM_ACTION_TYPE,
  IPageJumpRouteState,
  ITransfer,
  REPLENISH_APPLY_ITEMS_SCOPE,
  COMMON_ACTION_TYPES_REJECT,
  DID_KEYWORD_MAPPING,
  WANT_TO_KEYWORD_MAPPING,
  TRANSFER_PDF_HEADER_VALUES,
} from '@shared/types';
import { createFormRules as createTransferGroupAutoRestockFormRules } from './form-rules';
import { lazyInject } from '@app/containers/base/decorators';
import {
  BTN_CANCEL,
  BTN_OK,
  BUTTON_ACTIONS,
} from '@app/text-content/button-templates/button-templates-content';
import { TRANSFER_GROUP_MODULE } from '@app/routes';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';

import { TransferBaseFormService } from '../transfer-common';
import { BehaviorSubject, from, iif, Observable, of, Subject } from 'rxjs';
import {
  filterSelectedSites,
  generateBulkUpdateConfirmMessageParam,
  getAutoCompleteTransferNextStatus,
  getEventRelatedAndWarehouseSites,
  addParQtyColumn,
  addQtyPulledColumn,
} from '../transfer/transfer.util';
import { getSiteSelectorButtonCaption } from '@app/shared/utils/site-selector.utils';
import { getSiteSelectorColumnsDefinition } from '@app/shared/utils/site-selector-columns-definition-utils';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { getSubtractDateString, toDate } from '@app/shared/utils/date-utils';
import {
  observableCombineLatest,
  observableOf,
  observableThrowError,
  rx,
} from '@app/shared/rx';
import {
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  ONLY_DATE_SUBSTRING_LENGTH,
} from '@config/global-constants';
import { isEventType } from '../transfer/transfer-type.utils';
import {
  generateObjectId,
  getObservableValue,
} from '@app/shared/utils/common-utils';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { TOASTER_ERROR_MESSAGE } from '@app/shared/constants';
import { CHANGE_STATUS_TOTAL_STEPS } from '@shared/services/transfer/transfer-constants';
import {
  XUX_GRID_ACTIONS,
  XuxGridAction,
  ToolbarButtonDefinition,
} from '@geniuspos/ui-shared';
import { XuxEditGridComponent } from '@components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import {
  composeWithDefaultEventQuery,
  getTransferEventQueryFilter,
} from '../transfer/transfer.query.util';
import {
  TRANSFER_SAVED_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { getSiteDepartmentCode } from '@app/shared/utils/department-code.utils';
import { IPdfDefinitions } from '@app/shared/common/make-pdf/v2';
import { TRANSFER_FORM_FIELD_PATHS } from '../transfer/transfer-form-field-paths';
import { HttpErrorResponse } from '@angular/common/http';
import { TaskService } from '../task';
import { generateProgressBarLoadingParams } from '@app/shared/utils/task.utils';
import {
  markTransferItemsAcknowledge,
  unmarkTransferItemsAcknowledge,
  pushNewTransfersToDoc,
} from '@shared/services/transfer-group/utils/transfer-items-utils';
import { getUnsavedChangeInfo } from '@app/shared/utils';
import { takeUntil } from 'rxjs/operators';
import { calcDepartmentCosts } from '../transfer-group/utils/gand-totals.utils';
import { getSaveBeforeProcessModal } from '../transfer-group/modal-configs/unsaved-change.modal-config';
import { MAX_SITES_LIMIT } from '../transfer-group/transfer-group.constants';
import { TransferAutoRestockFormService } from '../transfer-auto-restock/transfer-auto-restock.form.service';
import { TransferAutoRestockService } from '../transfer-auto-restock/transfer-auto-restock.service';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { TransferGroupAutoRestockService } from './transfer-group-auto-restock.service';
import { TransferDestinationsFormService } from '../transfer-destinations/transfer-destinations.form.service';
import {
  IAfterReplenishParams,
  IReplenishDestination,
} from '../replenish-worker/replenish.types';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish-worker/replenish.constants';
import { BULK_REPLENISH_CONFIG_MAPPING } from '../transfer/transfer-replenish.constants';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferGroupAutoRestockFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER_GROUP;

  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TransferGroupAutoRestockService)
  public transferGroupAutoRestockService: TransferGroupAutoRestockService;
  @lazyInject(TaskService) public taskService: TaskService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(TransferAutoRestockFormService)
  public transferAutoRestockFormService: TransferAutoRestockFormService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;
  @lazyInject(TransferDestinationsFormService)
  public transferDestinationsFormService: TransferDestinationsFormService;

  public formFieldPath: ITransferGroupFormFieldPath = {
    count: 'count',
    eventId: 'event_id',
    eventName: 'event_name',
    initialCount: 'initial_count',
    inventoryItemVenues: 'inventory_items',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    name: 'name',
    siteId: 'site_id',
    storeNumber: 'store_number',
    siteName: 'site_name',
    siteIdTo: 'site_id_to',
    transactionDate: 'transaction_date',
    transferStatus: 'status',
    transferType: 'transfer_type',
    virtualGrandTotals: 'virtual.grand_totals',
    virtualSelectedEvent: 'virtual.selected_event',
    virtualTransactionDate: 'virtual.transaction_date',
    virtualTransferItems: 'virtual.transfer_items',
    virtualReallyChangedTransfers: 'virtual.really_changed_transfers',
  };

  public createFormRulesFunction: CreateFormRules<TransferGroupAutoRestockFormService> =
    createTransferGroupAutoRestockFormRules;

  public selectedSite: ISite;
  public eventIdDataSource: DataSource;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };
  public isNewCreated: boolean = false;

  public virtualTransferItemsAction$: Subject<IWidgetAction> = new Subject();
  public selectedEvent$: BehaviorSubject<IEvent> = new BehaviorSubject(null);

  public sitesDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferSiteList$: BehaviorSubject<ISite[]> = new BehaviorSubject([]);
  public eventList$: Subject<IEvent[]> = new Subject();
  public transferDestinationsDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsSelectedCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsTitleTips$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public transferDestinationsDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(
        I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
        {
          count: 0,
          entities: this.i18nService.translate(
            I18n.EntityNames.SiteSingularAndPlural,
          ),
          selectedCount: 0,
        },
      ),
    );
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.siteSingularAndPlural,
        ),
      }),
    );
  public isGridBusy$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public isEventExpired$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public dmSyncFinish$: Subject<ITask> = new Subject();
  public disableAddSiteObs: Observable<boolean> = observableCombineLatest([
    this.transferSiteList$,
    this.isEventExpired$,
    this.isGridBusy$,
  ]).pipe(
    rx.map(
      ([transferSiteList, isEventExpired, isGridBusy]) =>
        !(!isEmpty(transferSiteList) && !isEventExpired) || isGridBusy,
    ),
  );
  public bulkReplenishToPARDisabled$: Subject<boolean> = new Subject();
  public hiddenDestinationNoSitesInfo$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public hiddenDestinationInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  // hide buttons of event is  closed and event date is not valid(date not expired)
  public hideRelatedButtonsOfInvalidEvent$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  private previouslyReplenishItemOption: REPLENISH_APPLY_ITEMS_SCOPE;
  private destinationGrid: XuxEditGridComponent;
  private actionMapperForTransfer: {
    [key: string]: {
      handler: Function;
      did: string;
      wantTo: string;
      dmSync?: boolean;
    };
  } = {
    [COMMON_ACTION_TYPES.Submit]: {
      handler: this.bulkUpdateTransfersStatus.bind(this, null),
      did: DID_KEYWORD_MAPPING.submitted,
      wantTo: WANT_TO_KEYWORD_MAPPING.submit,
      dmSync: true,
    },
    [COMMON_ACTION_TYPES.Cancel]: {
      handler: this.bulkUpdateTransfersStatus.bind(
        this,
        TRANSFER_STATUS_VALUE.Canceled,
      ),
      did: DID_KEYWORD_MAPPING.canceled,
      wantTo: WANT_TO_KEYWORD_MAPPING.cancel,
    },
    [COMMON_ACTION_TYPES_REJECT]: {
      handler: this.bulkUpdateTransfersStatus.bind(
        this,
        TRANSFER_STATUS_VALUE.Rejected,
      ),
      did: DID_KEYWORD_MAPPING.rejected,
      wantTo: WANT_TO_KEYWORD_MAPPING.reject,
    },
  };

  public constructor(public injector: Injector) {
    super(injector);
    this.initEventDataSource();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onFormReady(form: FormGroup): void {
    // Make sure to get the required data (such as siteList, eventList, selectedEvent) before shutting down the loader.
    // The loader will be shut down later in ensureAllDataIsFetched function。
    this.uiActions.preventDisableLoaderAfterRequestFinish(true);
    this.uiActions.preventLoaderCloseWhenTimeout(true);
    this.isQohFromVisible = this.stateService.getTransferDisplayQohFrom();

    this.initRouteStateSubscription();
    this.initTransferItemsActionSubs();
    this.initTransferItemsSubscription();
    this.initEventIdSubscription();
    this.ensureAllDataIsFetched();

    this.initDescriptionSubscriptions();
    this.initTransferDestinationsData();

    this.eventIdDataSource?.refresh();
  }

  public initRouteStateSubscription(): void {
    const subscription = this.routeState$.subscribe(
      (routeState: IPageJumpRouteState) => {
        this.isNewCreated = get(routeState, 'isNewCreated', false);

        if (!this.isNewCreated) {
          this.isGridBusy$.next(false);
        }

        if (!routeState?.storeForm) {
          return;
        }

        this.patchTransferGroupForm(routeState.storeForm);

        const unsavedChangeInfo = getUnsavedChangeInfo(
          this.getFormFieldValue<ITransferData[]>(
            this.formFieldPath.virtualTransferItems,
          ),
          { i18nService: this.i18nService },
        );

        this.transferDestinationsTitleTips$.next(
          unsavedChangeInfo.unsavedMessage,
        );

        const isFormDirty =
          (get(routeState, 'isFormDirty', false) &&
            this.getIsHaveNewTransferItems()) ||
          gt(unsavedChangeInfo.unsavedCount, 0);

        if (isFormDirty) {
          this.form.markAsDirty();
        }
      },
    );
    this.subscriptions.push(subscription);
  }

  public initTransferItemsActionSubs(): void {
    this.subscriptions.push(
      this.virtualTransferItemsAction$.subscribe((action) => {
        if (
          isEqual(EDIT_GRID_ITEM_ACTION_TYPE.GRID_ITEMS_REMOVED, action.type)
        ) {
          this.handleDestinationGridRemoveRow(action, this.destinationGrid);
        }
      }),
    );
  }

  public initTransferDestinationsData(): void {
    const subs = this.transferSiteList$.subscribe((allSites) => {
      // Avoid the value of virtualTransferDestinations influences the data of transferSiteList$
      if (this.isNewCreated) {
        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
          map(allSites, (site) => ({
            ...site,
            site_department_code_to: getSiteDepartmentCode(site),
          })),
          false,
        );
      }
    });

    const triggerNooSitesInfoSubs = this.transferSiteList$
      .pipe(rx.skip(1))
      .subscribe((allSites) => {
        this.hiddenDestinationNoSitesInfo$.next(!isEmpty(allSites));
      });

    this.subscriptions.push(subs);
    this.subscriptions.push(triggerNooSitesInfoSubs);
  }

  public ensureAllDataIsFetched(): void {
    const unsubscribe: Subject<void> = new Subject<void>();
    this.subscriptions.push(
      observableCombineLatest([
        this.eventList$.pipe(rx.startWith(null)),
        this.selectedEvent$,
        // need skip first value(first value is [])
        this.transferSiteList$.pipe(rx.skip(1)),
      ])
        .pipe(takeUntil(unsubscribe))
        .subscribe(
          ([eventList, selectedEvent, siteList]: [
            IEvent[],
            IEvent,
            ISite[],
          ]): void => {
            if (
              (!isNil(eventList) || !isNil(selectedEvent)) &&
              !isNil(siteList)
            ) {
              this.uiActions.preventDisableLoaderAfterRequestFinish(false);
              this.uiActions.preventLoaderCloseWhenTimeout(false);
              this.uiActions.disableLoader();
              unsubscribe.next();
            }
          },
        ),
    );
  }

  public destroyFormDefinition(): void {
    this.isQohFromVisible = false;
    this.selectedEvent$.next(null);
    this.transferSiteList$.next([]);
    this.isEventExpired$.next(false);
    this.transferDestinationsTitleTips$.next('');
    this.isGridBusy$.next(true);
    this.hiddenDestinationInfo$.next(true);
    this.hiddenDestinationNoSitesInfo$.next(true);
    this.hideRelatedButtonsOfInvalidEvent$.next(false);
    this.isNewCreated = false;
    this.uiActions.preventDisableLoaderAfterRequestFinish(false);
    this.uiActions.preventLoaderCloseWhenTimeout(false);
    super.destroyFormDefinition();
  }

  public initTransferItemsSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.virtualTransferItems).subscribe(
        (transferItems) => {
          const filteredOutDisabledItems = filter(transferItems, {
            disabled: false,
          });

          this.bulkReplenishToPARDisabled$.next(
            isEmpty(filteredOutDisabledItems) ||
              every(filteredOutDisabledItems, (item) =>
                includes(TRANSFER_SAVED_STATUS, item.status),
              ),
          );

          const matchSite = find(transferItems, (site) =>
            this.noPrimaryWarehouse(site),
          );

          this.hiddenDestinationInfo$.next(!matchSite);

          this.updateGrandTotalsData(transferItems);
          this.generateVoidAllowedMap(transferItems);
        },
      ),
    );
  }

  public initEventIdSubscription(): void {
    const subscription = observableCombineLatest([
      this.eventList$,
      this.fieldValueChanges(this.formFieldPath.eventId),
    ]).subscribe(([events, eventId]: [IEvent[], string]) => {
      const selectedEvent = find(events, { id: eventId });
      this.selectedEvent$.next(selectedEvent);
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualSelectedEvent,
        selectedEvent,
        false,
      );
      this.getTransferSiteList();
    });
    this.subscriptions.push(subscription);
  }

  public updateGrandTotalsData(data: ITransferDestination[]): void {
    const destinationsWithNewStatus = filter(data, {
      status: TRANSFER_STATUS_VALUE.New,
    });
    const virtualTransferItems = get(
      this.document,
      this.formFieldPath.virtualTransferItems,
      [],
    );
    const needCalculatedItems = concat(
      destinationsWithNewStatus,
      virtualTransferItems,
    );

    const virtualReallyChangedTransfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    forEach(needCalculatedItems, (transferItem) => {
      const siteIdToValue = get(transferItem, this.formFieldPath.siteIdTo);
      const reallyChangedTransfer = get(
        virtualReallyChangedTransfers,
        siteIdToValue,
      );

      if (isEmpty(reallyChangedTransfer?.items)) {
        return;
      }

      merge(
        transferItem,
        get(
          virtualReallyChangedTransfers,
          [get(transferItem, this.formFieldPath.siteIdTo), 'grand_total'],
          {},
        ),
      );
    });

    const departmentTotal: ITransferGrandTotal[] = calcDepartmentCosts(
      needCalculatedItems,
      this.i18nService,
    );

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualGrandTotals,
      departmentTotal,
      false,
    );
  }

  public isTransferGroupItemsEmpty(): boolean {
    if (!this.form) {
      return true;
    }

    return isEmpty(
      this.getFormFieldValue(this.formFieldPath.virtualTransferItems),
    );
  }

  public fetchSiteList(): Promise<any> {
    if (!this.form) {
      this.isGridBusy$.next(false);

      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const eventIdValue = this.getFormFieldValue(this.formFieldPath.eventId);

    const queryWithDefaults: IFilterQuery = {
      filter: `is_active eq true`,
      select: [
        'name',
        'id',
        'type',
        'business_type',
        'store_number',
        'metadata',
      ],
      filter_by_assigned_sites: true,
      include_inactive: false,
      top: MAX_PAGE_SIZE,
    };

    if (isEventType(this.getFormTransferType())) {
      if (isNil(eventIdValue)) {
        this.isGridBusy$.next(false);

        return Promise.resolve({
          items: [],
        } as IApiListResponse<any>);
      }
      queryWithDefaults.top = MAX_PAGE_SIZE;
    }

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.switchMap((allSites) => {
          const eventSites: ISite[] = get(
            getObservableValue(this.selectedEvent$),
            'available_sites',
            [],
          );

          const sites: ISite[] = allSites.items as ISite[];

          const sitesList = getEventRelatedAndWarehouseSites(sites, eventSites);

          const siteIds: string[] = map(sitesList, 'id');

          return observableCombineLatest([
            observableOf(sitesList),
            this.dmDataService.getSiteGroupId(siteIds),
          ]);
        }),
        rx.switchMap(([sites, siteGroupId]) => {
          const sitePreferences =
            this.dmDataService.getSitesPreferencesBySiteGroupId(siteGroupId);

          return observableCombineLatest([
            observableOf(sites),
            sitePreferences,
          ]);
        }),
        rx.map(([sites, preferences]) => {
          const response = {
            total: 0,
            items: [],
          };

          if (!this.form) return { ...response, items: [] };

          const convertedSites = this.convertSiteNameToSiteNameTo(sites);

          const setWarehouseForSites =
            this.transferAutoRestockFormService.setPrimaryWarehousetoSites(
              convertedSites,
              preferences,
            );
          const sitesList =
            this.transferAutoRestockFormService.setSitesPrimaryWarehouseTop(
              setWarehouseForSites,
            );
          response.total = size(sitesList);
          response.items = sitesList;

          this.isGridBusy$.next(false);

          return response;
        }),
        rx.finalize(() => {
          this.isGridBusy$.next(false);
        }),
      )
      .toPromise();
  }

  public convertSiteNameToSiteNameTo(sites) {
    return map(sites, (site) => {
      return {
        ...site,
        store_number_to: site.store_number,
        site_id_to: site.id,
        site_name_to: site.name,
        status: TRANSFER_STATUS_VALUE.Null,
      };
    });
  }

  public isRowSelectable(params): boolean {
    return !this.noPrimaryWarehouse(params.data);
  }

  public noPrimaryWarehouse(data): boolean {
    return data.disabled;
  }

  public async checkAndHandleEventExpired(
    response: IApiListResponse<IBaseEntityType>,
  ): Promise<IApiListResponse<IBaseEntityType>> {
    const eventId = getFormFieldValue(this.form, this.formFieldPath.eventId);

    if (isNil(find(response.items, { id: eventId }))) {
      this.isEventExpired$.next(true);

      return this.eventIdDataSource.service
        .fetchList({
          filter: `_id eq objectid(${eventId})`,
          include_inactive: true,
          include_available_sites: true,
        })
        .pipe(rx.first())
        .toPromise();
    }

    return response;
  }

  public eventDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    const transferTypeValue: TRANSFER_TYPE = this.getFormTransferType();

    if (!this.form || !isEventType(transferTypeValue)) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const userQuery = {
      ...query,
      filter: getTransferEventQueryFilter(transferTypeValue),
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map(async (response: IApiListResponse<IBaseEntityType>) => {
          if (isNil(this.form)) return response;
          const newResponse: IApiListResponse<IBaseEntityType> =
            await this.checkAndHandleEventExpired(response);
          this.eventList$.next(newResponse.items as IEvent[]);

          return newResponse;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public initDescriptionSubscriptions(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.transferDestinationsDisplayCount$,
        this.transferDestinationsSelectedCount$,
      ])
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe(
          ([displayedCount, selectedCount]: [number, number]): void => {
            const sitesSubTitle = this.i18nService.instant(
              I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
              {
                count: displayedCount,
                entities: this.i18nService.translate(
                  I18n.EntityNames.SiteSingularAndPlural,
                ),
                selectedCount,
              },
            );
            this.transferDestinationsDescription$.next(sitesSubTitle);
          },
        ),
    );
    this.subscriptions.push(
      this.sitesDisplayCount$.subscribe((count: number): void => {
        const sitesSubTitle = this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntities,
          {
            count,
            entities: this.i18nService.instant(
              I18n.EntityNames.siteSingularAndPlural,
            ),
          },
        );
        this.siteModalDescription$.next(sitesSubTitle);
      }),
    );
  }

  public async getTransferSiteList(): Promise<void> {
    const response = await this.fetchSiteList().then((result) => result);
    this.transferSiteList$.next(response.items as ISite[]);
  }

  public getIsHaveNewTransferItems(): boolean {
    return some(
      this.getFormFieldValue(this.formFieldPath.virtualTransferItems),
      (transferItem: ITransferData) =>
        includes(TRANSFER_UNSAVED_STATUS, transferItem.status),
    );
  }

  public get groupFields(): string[] {
    return [this.formFieldPath.name];
  }

  public getGroupInfoDirtyFields(): string[] {
    return filter(this.groupFields, (path) => this.getFormFieldDirty(path));
  }

  public async checkAndSaveGroupInfo(
    gridDirtyFields: string[],
  ): Promise<boolean> {
    let checkPassOrSaved = isEmpty(gridDirtyFields);

    try {
      // dirty, need save group info first
      if (!checkPassOrSaved) {
        const modalConfig = getSaveBeforeProcessModal(this.i18nService);
        const confirmedResult = await this.uiActions.confirm(modalConfig);

        if (!confirmedResult.confirmed) {
          return checkPassOrSaved;
        }

        this.uiActions.enableLoader();

        // save group info
        await this.transferGroupAutoRestockService
          .updateEntity(pick(this.form.value, gridDirtyFields), {
            endpoint: this.document._id,
            suppressSuccessMessage: true,
          })
          .pipe(rx.first())
          .toPromise();

        // clear all group dirty status
        forEach(this.groupFields, (fieldPath) => {
          const fieldControl = this.getFormFieldControl(fieldPath);

          fieldControl?.markAsPristine();
          fieldControl?.markAsUntouched();
        });

        this.uiActions.disableLoader();

        checkPassOrSaved = true;
      }
    } catch (error) {
      this.uiActions.disableLoader();

      if (error instanceof ProgressEvent) {
        this.uiActions.showErrorToasterMessage({
          title: this.i18nService.instant(
            I18n.ErrorMessages.APIRequestTimeoutTitle,
          ),
          message: this.i18nService.instant(
            I18n.ErrorMessages.XVIAPIRequestError,
          ),
        });

        return checkPassOrSaved;
      }

      const toast = {
        errors: {
          title: error.title,
          message: error?.meta?.message || '',
        },
      };

      this.uiActions.showErrorToasterMessage(toast);
    }

    return checkPassOrSaved;
  }

  public async editSiteInventoryItems(
    gridItem: ITransferData,
    needDmSync: boolean = true,
  ): Promise<void> {
    const groupDirtyFields = this.getGroupInfoDirtyFields();
    const isPendingOrOpenTransfer = [
      TRANSFER_STATUS_VALUE.Open,
      TRANSFER_STATUS_VALUE.Pending,
    ].includes(gridItem.status);

    const checkGroupSaveInfoResult =
      await this.checkAndSaveGroupInfo(groupDirtyFields);

    if (!checkGroupSaveInfoResult) {
      return;
    }

    const routeUri = `${TRANSFER_GROUP_MODULE}/${this.document._id}/${
      includes(TRANSFER_UNSAVED_STATUS, gridItem.status) ? 'add' : gridItem._id
    }?get_updated_items=${needDmSync ? isPendingOrOpenTransfer : false}`;

    this.uiActions.removeNotificationsOnPageLeave();
    const fromStoreNumber = get(
      find(getObservableValue(this.transferSiteList$), (site) =>
        isEqual(site.id, gridItem.site_id),
      ),
      'store_number',
      get(gridItem, 'store_number'),
    );

    const toStoreNumber = get(
      find(getObservableValue(this.transferSiteList$), (site) =>
        isEqual(site.id, gridItem.site_id_to),
      ),
      'store_number_to',
      get(gridItem, 'store_number_to'),
    );

    this.uiActions.openRoute(routeUri, {
      state: {
        isFormDirty: this.form.dirty,
        // when go to next page, will omit these field to get the new inventory item data
        // when back to this page, will re-call replenish api to get thresholds data
        selectedSite: omit(gridItem, [
          'inventory_items',
          'noAvailablePAR',
          'skipReplenish',
        ]),
        isEventExpired: getObservableValue(this.isEventExpired$),
        storeForm: merge(
          {},
          {
            site_department_code_to: gridItem.site_department_code_to,
            site_name_to: gridItem.site_name_to,
            site_id_to: gridItem.site_id_to,
            status: gridItem.status,
            transfer_group_entity_id: this.document.entity_id,
            virtual: {
              transfer_group_name: this.getFormFieldValue(
                this.formFieldPath.name,
              ),
              transfer_items: map(
                getFormFieldValue(
                  this.form,
                  this.formFieldPath.virtualTransferItems,
                ),
                (transfer) =>
                  omit(transfer, [
                    'inventory_items',
                    'noAvailablePAR',
                    'skipReplenish',
                  ]),
              ),
            },
          },
          omit(this.form.value, ['virtual.transfer_items']),
          {
            site_id: gridItem.site_id,
            virtual: {
              from_site_name: `${get(gridItem, 'site_name')} [${fromStoreNumber}]`,
              to_site_name: `${get(gridItem, 'site_name_to')} [${toStoreNumber}]`,
            },
          },
        ),
      },
    });
  }

  public clearUnsavedChangesBySaveResult(
    bulkOperationResult: IBulkUpdateDocumentsResult<IBaseEntityType>,
  ): void {
    const savedDocuments = compact(
      concat(
        bulkOperationResult.created_documents,
        bulkOperationResult.updated_documents,
      ),
    );

    if (isEmpty(savedDocuments)) {
      return;
    }

    const transferItems: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    const siteIds = compact(
      map(savedDocuments, (savedDoc) =>
        has(savedDoc, 'site_id_to')
          ? savedDoc.site_id_to
          : find(
              transferItems,
              (virtualTransfer: ITransferData) =>
                virtualTransfer._id === savedDoc._id,
            )?.site_id_to,
      ),
    );

    return this.clearUnsavedChanges(siteIds);
  }

  public clearUnsavedChanges(siteIds: string[]): void {
    if (isEmpty(siteIds)) {
      return;
    }

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    if (isEmpty(reallyChangedTransfers)) {
      return;
    }

    const transferItemsData: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    forEach(reallyChangedTransfers, (_, key) => {
      if (!includes(siteIds, key)) {
        return;
      }

      unset(reallyChangedTransfers, key);

      set(find(transferItemsData, { site_id_to: key }), 'isChanged', false);
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      reallyChangedTransfers,
      false,
    );
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      transferItemsData,
      false,
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      { i18nService: this.i18nService },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);

    if (
      isEqual(unsavedChangeInfo.unsavedCount, 0) &&
      !this.getFormFieldDirty(this.formFieldPath.virtualTransferItems) &&
      isEmpty(this.getGroupInfoDirtyFields())
    ) {
      // form not empty, set form markAsPristine
      this.form.markAsPristine();
    }
  }

  public beforeUpdateAction(
    items: ITransferData[],
    action: string,
  ): Promise<IBulkUpdateDocumentsResult> {
    const defaultUpdateResult: IBulkUpdateDocumentsResult = {
      successCount: 0,
      totalCount: 0,
      updated_documents: [],
    };

    if (
      isEmpty(
        this.getFormFieldValue(
          this.formFieldPath.virtualReallyChangedTransfers,
        ),
      )
    ) {
      return Promise.resolve(defaultUpdateResult);
    }

    const actionHandlerMapping = {
      [COMMON_ACTION_TYPES.Cancel]: (): Promise<IBulkUpdateDocumentsResult> => {
        return Promise.resolve(
          merge(defaultUpdateResult, {
            updated_documents: filter(items, {
              status: TRANSFER_STATUS_VALUE.Open,
            }),
          }),
        );
      },
    };

    if (has(actionHandlerMapping, action)) {
      return actionHandlerMapping[action](items);
    }

    return Promise.resolve(defaultUpdateResult);
  }

  public async bulkUpdateTransfer(
    gridItem: ITransferData[],
    action: string,
    refreshHistory: Function,
    handleTransferGroup: boolean = false,
  ): Promise<boolean> {
    let beUpdatedItems = gridItem;
    const isMultiple = beUpdatedItems.length > 1 || handleTransferGroup;
    const currentAction = this.actionMapperForTransfer[action];
    const needCreateTransfers = this.getNeedCreateTransfers(beUpdatedItems);

    beUpdatedItems = isEqual(action, COMMON_ACTION_TYPES.Cancel)
      ? beUpdatedItems
      : this.combineChangesWithOriginItems(
          beUpdatedItems,
          this.getFormFieldValue(
            this.formFieldPath.virtualReallyChangedTransfers,
          ),
        );
    if (action === COMMON_ACTION_TYPES_REJECT) {
      beUpdatedItems =
        this.transferService.getPreRejectTransfers(beUpdatedItems);
    }

    if (currentAction.dmSync && !isMultiple && isEmpty(needCreateTransfers)) {
      const syncResult: ITask = await this.taskService
        .getDmSyncResult(beUpdatedItems, { isSync: true })
        .pipe(
          rx.catchError((error: HttpErrorResponse) => {
            this.uiActions.showToasterMessage({
              type: NotificationType.ERROR,
              title: TOASTER_ERROR_MESSAGE.title,
              body: this.i18nService.instant(
                I18n.ErrorMessages.CheckInternetAndTryAgain,
              ),
            });

            return observableThrowError(error);
          }),
        )
        .toPromise();
      const subTask: any = head(syncResult?.sub_tasks || []);
      if (
        isEqual('completed', subTask.status) &&
        subTask.value.is_acknowledge
      ) {
        await this.editSiteInventoryItems(beUpdatedItems[0], false);

        return false;
      }
    }

    const message = isMultiple
      ? this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateHowManyTarget,
          generateBulkUpdateConfirmMessageParam({
            action,
            did: null,
            count: beUpdatedItems.length,
            target: I18n.Forms.TransferForm.transfersBrackets,
            i18nService: this.i18nService,
          }),
        )
      : this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
          {
            target: this.i18nService.instant(I18n.Forms.TransferForm.transfer),
            wantTo: this.i18nService.instant(action),
          },
        );

    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return false;
    }

    await this.beforeUpdateAction(beUpdatedItems, action);

    let bulkCreateResult: IBulkUpdateDocumentsResult;
    if (
      isEqual(action, COMMON_ACTION_TYPES.Submit) &&
      !isEmpty(needCreateTransfers)
    ) {
      bulkCreateResult = await this.bulkSaveTransfers(
        needCreateTransfers,
        true,
      );

      beUpdatedItems = map(beUpdatedItems, (item) => {
        const newItemDocument = find(bulkCreateResult.created_documents, {
          site_id_to: item.site_id_to,
        });
        if (!isNil(newItemDocument)) {
          // unset inventory items to avoid save when before change status
          unset(item, 'inventory_items');
          item._id = newItemDocument._id;
        }

        return item;
      });

      const data = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
      );
      const newData = map(data, (item) => {
        const newItemDocument = find(bulkCreateResult.created_documents, {
          site_id_to: item.site_id_to,
        });
        let newItem = item;
        if (!isNil(newItemDocument)) {
          newItem = merge(
            {},
            item,
            omit(newItemDocument, ['type', 'store_number']),
          );

          newItem = omit(newItem, ['inventory_items']);

          // need call this for grand total calc(grand total depend on document transfers)
          pushNewTransfersToDoc(this.document, [newItem]);

          return newItem;
        }

        return newItem;
      });
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
        newData,
        false,
      );
      if (!eq(bulkCreateResult.totalCount, bulkCreateResult.successCount)) {
        this.clearUnsavedChangesBySaveResult(bulkCreateResult);
        refreshHistory();
        await this.refreshGrandTotal();
        this.updateSpecifiedTransferItemsStatus(
          bulkCreateResult.created_documents,
        );

        this.transferGroupAutoRestockService.shownUpdateMessage(
          {
            did: DID_KEYWORD_MAPPING.saved,
            wantTo: WANT_TO_KEYWORD_MAPPING.save,
            isSingle: !isMultiple,
          },
          bulkCreateResult,
        );

        return;
      }
    }

    if (isMultiple) {
      this.uiActions.enableLoader(
        generateProgressBarLoadingParams({ i18nService: this.i18nService }),
      );
      this.uiActions.preventLoaderCloseWhenTimeout(true);
    }

    const isAccept = isEqual(action, COMMON_ACTION_TYPES.Accept);
    if (isAccept) {
      this.uiActions.preventDisableLoaderAfterRequestFinish(isAccept);
      this.uiActions.preventLoaderCloseWhenTimeout(isAccept);
      this.uiActions.enableLoader();
    }

    iif(
      () => isMultiple && currentAction.dmSync,
      of(true).pipe(
        rx.mergeMap(() =>
          this.taskService
            .getDmSyncResult(beUpdatedItems, {
              noLoader: true,
              stepCount: CHANGE_STATUS_TOTAL_STEPS,
            })
            .pipe(
              rx.mergeMap((taskResult: ITask) => {
                const dmSyncTransferIds = map(
                  filter(map(taskResult.sub_tasks, 'value'), 'is_acknowledge'),
                  'transfer_id',
                );

                markTransferItemsAcknowledge(
                  dmSyncTransferIds,
                  this.form,
                  this.formFieldPath,
                );

                return currentAction.handler(
                  filter(
                    beUpdatedItems,
                    (transfer: ITransferData) =>
                      !dmSyncTransferIds.includes(get(transfer, '_id')),
                  ),
                  {
                    noLoader: true,
                    currentStep: CHANGE_STATUS_TOTAL_STEPS,
                    stepCount: CHANGE_STATUS_TOTAL_STEPS,
                  },
                );
              }),
              rx.map((processResult: IBulkUpdateDocumentsResult) => {
                return {
                  ...processResult,
                  totalCount: size(beUpdatedItems),
                };
              }),
            ),
        ),
      ),
      of(true).pipe(
        rx.mergeMap(() =>
          currentAction.handler(beUpdatedItems, {
            isSync: !isMultiple,
            noLoader: isEqual(action, COMMON_ACTION_TYPES.Accept) || isMultiple,
          }),
        ),
        rx.tap(({ updated_documents }) => {
          unmarkTransferItemsAcknowledge(
            map(updated_documents, '_id'),
            this.form,
            this.formFieldPath,
          );
        }),
      ),
    )
      .pipe(
        rx.mergeMap((bulkUpdateResult: IBulkUpdateDocumentsResult) => {
          return !isAccept
            ? of(bulkUpdateResult)
            : from(
                this.generateVoidAllowedMap(
                  this.getUpdateItem(bulkUpdateResult),
                ),
              ).pipe(rx.map(() => bulkUpdateResult));
        }),
        rx.catchError((error: HttpErrorResponse) => {
          this.uiActions.showToasterMessage({
            type: NotificationType.ERROR,
            title: TOASTER_ERROR_MESSAGE.title,
            body: this.i18nService.instant(
              I18n.ErrorMessages.CheckInternetAndTryAgain,
            ),
          });

          return observableThrowError(error);
        }),
      )
      .subscribe((bulkUpdateResult: IBulkUpdateDocumentsResult) => {
        this.clearUnsavedChangesBySaveResult(bulkUpdateResult);
        refreshHistory();

        const data = getFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
        );

        const newData = map(data, (item) => {
          return {
            ...item,
            status:
              find(bulkUpdateResult.updated_documents, { _id: item._id })
                ?.status ?? item.status,
          };
        });

        this.refreshGrandTotal({ noLoader: isMultiple });

        this.transferGroupAutoRestockService.shownUpdateMessage(
          {
            did: currentAction.did,
            wantTo: currentAction.wantTo,
            isSingle: !isMultiple,
          },
          bulkUpdateResult,
        );

        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
          newData,
          false,
        );
        if (isAccept) {
          this.uiActions.disableLoader();
          this.uiActions.preventDisableLoaderAfterRequestFinish(false);
          this.uiActions.preventLoaderCloseWhenTimeout(false);
        }
        if (isMultiple) {
          this.uiActions.disableLoader();
        }
      });
  }

  public getUpdateItem(
    bulkUpdateResult: IBulkUpdateDocumentsResult,
  ): ITransferData[] {
    const data = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return map(data, (item) => {
      return {
        ...item,
        status:
          find(bulkUpdateResult.updated_documents, { _id: item._id })?.status ??
          item.status,
      };
    });
  }

  public async bulkSaveTransfers(
    transferDestinations: ITransferData[],
    noLoader: boolean = false,
  ): Promise<IBulkUpdateDocumentsResult> {
    if (!noLoader) {
      this.uiActions.enableLoader();
      this.uiActions.preventLoaderCloseWhenTimeout(true);
    }
    const newDocument = Object.assign({}, this.form.value);
    const requestFields = [
      'event_id',
      'inventory_items',
      'site_id',
      'site_id_to',
      'status',
      'transaction_date',
      'virtual.transaction_date',
      'transaction_id',
      'transfer_type',
      'type',
      'transfer_group_entity_id',
    ];

    const requestDocuments = map(transferDestinations, (item) => {
      const newEntityId = generateObjectId();
      const newItem = pick(newDocument, requestFields);
      set(newItem, 'inventory_items', item.inventory_items);
      set(newItem, 'site_id_to', item.id);
      set(newItem, 'transaction_id', newEntityId);
      set(newItem, 'type', ENTITY_TYPES.TRANSFER);
      set(
        newItem,
        'transfer_group_entity_id',
        get(this.document, ENTITY_ID_PATH),
      );
      set(newItem, 'status', TRANSFER_STATUS_VALUE.Null);
      set(newItem, 'site_id', item.site_id);

      return newItem;
    });

    return this.transferService
      .bulkCreateEntity(requestDocuments, {})
      .pipe(
        rx.finalize(() => {
          if (!noLoader) {
            this.uiActions.disableLoader();
          }
        }),
      )
      .toPromise();
  }

  public updateSpecifiedTransferItemsStatus(
    transfers: IBaseEntityType[],
  ): void {
    const virtualTransferItems: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    const newData = map(virtualTransferItems, (item) => {
      const newTransfer = find(transfers, { _id: item._id });

      // new Transfer status is same as completed, need disable void
      if (isEqual(TRANSFER_STATUS_VALUE.Completed, newTransfer?.status)) {
        set(this.voidAllowedMap, item._id, false);
      }

      return {
        ...item,
        status: newTransfer?.status ?? item.status,
      };
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      newData,
      false,
    );
  }

  public bulkUpdateTransfersStatus(
    defaultTargetStatus: TRANSFER_STATUS_VALUE,
    needSubmitTransfers: ITransferData[],
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult> {
    const updateTransferStatus = getAutoCompleteTransferNextStatus(
      this.getFormTransferType(),
      TRANSFER_STATUS_VALUE.Open,
      this.stateService,
    );
    const targetStatus = defaultTargetStatus || updateTransferStatus;

    return this.transferGroupAutoRestockService.bulkUpdateStatus(
      targetStatus,
      needSubmitTransfers,
      options,
    );
  }

  public async openSiteSelector(): Promise<void> {
    const groupDirtyFields = this.getGroupInfoDirtyFields();

    const checkGroupSaveInfoResult =
      await this.checkAndSaveGroupInfo(groupDirtyFields);

    if (!checkGroupSaveInfoResult) {
      return;
    }

    const virtualTransferItems = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    const transferSitesList$: Observable<ISite[]> = this.transferSiteList$
      .first()
      .map((sites) => {
        const transferItems = getFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
        );

        return filterSelectedSites(
          [...map(transferItems, this.formFieldPath.siteIdTo)],
          sites,
        );
      });
    const transferSitesListPromise: ISite[] =
      await transferSitesList$.toPromise();
    const entityIdsForMetadataList: EntityId[] = chain(transferSitesListPromise)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const siteSelectorGridSettings: IModalDialogOptions = {
      title: I18n.Common.SiteSelector,
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: (selectedModalSites) =>
            getSiteSelectorButtonCaption({
              ...selectedModalSites,
              i18nService: this.i18nService,
            }),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          modified_message: { type: 'string' },
          items: { type: 'array', required: true },
        },
      },
      formRules: {
        fields: {
          items: {
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              displayCountObs: this.sitesDisplayCount$,
              valueField: 'id',
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              searchPlaceholder: I18n.Common.Search,
              columns: getSiteSelectorColumnsDefinition(
                entityIdsForMetadataList,
              ),
              userGridOptions: {
                overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
              },
            },
          },
        },
      },
    };

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(transferSitesList$, this.i18nService),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;

    const additionalItems = map(value.items, (item) => ({
      ...item,
      site_department_code_to: getSiteDepartmentCode(item),
      site_id_to: get(item, 'id'),
      store_number_to: get(item, 'store_number'),
      site_name_to: get(item, 'name'),
      status: TRANSFER_STATUS_VALUE.Null,
      error: false,
    }));

    const primaryAndWarehouseTopSites: ISite[] = concat(
      additionalItems,
      virtualTransferItems,
    );

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      primaryAndWarehouseTopSites,
    );
  }

  public composeConfirmationModalOptions(action: string): IModalDialogOptions {
    if (action === BUTTON_ACTIONS.REMOVE) {
      return {
        text: I18n.InfoMessages.ConfirmationModalRemovesSites,
      };
    }
  }

  public getOpenedTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): any {
    return filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Open),
    );
  }

  /**
   * get all need update transfers
   * will return all open/pending changed transfers and will return [] when there is no any changes
   *
   * @param containsStatus only include transfer status
   */
  public getNeedUpdateTransfers(
    containsStatus?: TRANSFER_STATUS_VALUE | TRANSFER_STATUS_VALUE[],
  ): ITransferData[] {
    const includeStatusList = compact(
      isArray(containsStatus) ? containsStatus : [containsStatus],
    );

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    if (isEmpty(reallyChangedTransfers)) {
      return [];
    }

    const reallyChangedSiteIdToList = keys(reallyChangedTransfers);
    const allTransfers: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    const changedTransfers = filter(allTransfers, (transfer) => {
      const siteIdTo = transfer.site_id_to;

      const isSavedTransfer = !includes(
        TRANSFER_UNSAVED_STATUS,
        transfer.status,
      );
      const isIncludeTransfer = isEmpty(includeStatusList)
        ? true
        : includes(includeStatusList, transfer.status);
      const isChangedTransfer = includes(reallyChangedSiteIdToList, siteIdTo);

      return isIncludeTransfer && isSavedTransfer && isChangedTransfer;
    });

    return this.combineChangesWithOriginItems(
      changedTransfers,
      reallyChangedTransfers,
    );
  }

  /**
   * combine local unsaved changes and original items to transfers
   *
   * @param targetTransfers target transfers
   * @param reallyChangedTransfers local unsaved changes(with original items)
   */
  public combineChangesWithOriginItems(
    targetTransfers: ITransferData[],
    reallyChangedTransfers: ReallyChangedTransfers,
  ): ITransferData[] {
    if (isEmpty(targetTransfers) || isEmpty(reallyChangedTransfers)) {
      return targetTransfers;
    }

    return map(targetTransfers, (transfer) => {
      const siteIdTo = transfer.site_id_to;
      const changedTransfer = get(reallyChangedTransfers, siteIdTo, {});
      const changedInventoryItems = get(changedTransfer, 'items', []);
      const changedInventoryItemIds = map(
        changedInventoryItems,
        'item_entity_id',
      );
      const originInventoryItems = get(changedTransfer, 'originItems', []);

      const notChangedInventoryItems = filter(
        originInventoryItems,
        (originItem) => {
          return !includes(changedInventoryItemIds, originItem.item_entity_id);
        },
      );

      const inventoryItems = isEmpty(get(changedTransfer, 'originItems', []))
        ? changedInventoryItems
        : concat(
            notChangedInventoryItems,
            map(changedInventoryItems, (changedItem) => {
              const originItem = find(originInventoryItems, {
                item_entity_id: changedItem.item_entity_id,
              });

              return {
                ...(originItem ?? {}),
                ...changedItem,
              };
            }),
          );

      return assignIn({}, transfer, {
        inventory_items: inventoryItems,
      });
    });
  }

  public getNeedCreateTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    const newTransfers = filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.New),
    );

    return this.combineChangesWithOriginItems(
      newTransfers,
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers),
    );
  }

  public getNeedSubmitTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    const needSubmitTransfers = filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Open),
    );

    return this.combineChangesWithOriginItems(
      needSubmitTransfers,
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers),
    );
  }

  public getNeedRejectTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    const needSubmitTransfers = filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Pending),
    );

    return this.combineChangesWithOriginItems(
      needSubmitTransfers,
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers),
    );
  }

  public getUnsavedDestinations(): ITransferDestination[] {
    const virtualTransferDestinations: ITransferDestination[] =
      getFormFieldValue(this.form, this.formFieldPath.virtualTransferItems);

    return filter(virtualTransferDestinations, (item) =>
      includes(TRANSFER_UNSAVED_STATUS, item.status),
    );
  }

  public getEventForReplenish(): object {
    return {
      event: getObservableValue(this.selectedEvent$),
    };
  }

  public getUnsavedDataForReplenish(): object {
    const unSavedData: ReallyChangedTransfers = this.getFormFieldValue(
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    return {
      unSavedData,
    };
  }

  public beforeReplenish(): void {
    this.destinationGrid.xuxGridComponent.gridApi.setFilterModel(null);
  }

  public afterReplenish(params: IAfterReplenishParams): void {
    const destinations = get(
      params,
      'destinations',
      [],
    ) as IReplenishDestination[];
    const reallyChangedTransfers: ReallyChangedTransfers = reduce(
      destinations,
      (changedTransfers, destination) => {
        if (
          isNil(destination.reallyChangedItems) ||
          isEmpty(destination.reallyChangedItems)
        ) {
          return changedTransfers;
        }

        changedTransfers[destination.toSite] = {
          items: destination.reallyChangedItems,
        };

        unset(destination, 'reallyChangedItems');

        return changedTransfers;
      },
      {},
    );

    const originReallyChangedTransfer = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    forEach(reallyChangedTransfers, (singleTransfer, field) => {
      if (!isEmpty(originReallyChangedTransfer[field])) {
        const mergedData = originReallyChangedTransfer[field].items;

        forEach(reallyChangedTransfers[field].items, (newItem) => {
          const targetItem = find(mergedData, {
            entity_id: newItem.entity_id,
          });
          if (!isNil(targetItem)) {
            merge(targetItem, newItem);
          } else {
            mergedData.push(newItem);
          }
        });
        originReallyChangedTransfer[field].items = mergedData;
      } else {
        originReallyChangedTransfer[field] = {
          ...singleTransfer,
        };
      }
    });

    const virtualTransferItems: any[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    forEach(destinations, (destination) => {
      const virtualTransferItem = find(virtualTransferItems, {
        id: destination.toSite,
      });

      if (!isNil(virtualTransferItem)) {
        merge(virtualTransferItem, destination);
        set(
          virtualTransferItem,
          'inventory_items',
          destination.inventory_items,
        );
      }
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      virtualTransferItems,
    );
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      originReallyChangedTransfer,
      false,
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      { i18nService: this.i18nService },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);
  }

  public getBulkReplenishClickHandler(btn: ToolbarButtonDefinition): Function {
    return async (): Promise<void> => {
      const btnReplenishConfig =
        BULK_REPLENISH_CONFIG_MAPPING[btn.caption as string];

      const unsavedTransfers = this.getUnsavedDestinations();

      const unsavedVirtualTransfers = filter(
        unsavedTransfers,
        (transfer) => !transfer.disabled,
      );

      await this.replenishWorkerBuilder
        .build({
          additionDataOpts: {
            preInventory: [
              this.getUnsavedDataForReplenish.bind(this),
              this.getEventForReplenish.bind(this),
            ],
            afterInventory: btnReplenishConfig.afterInventory,
          },
          on: {
            beforeReplenish: this.beforeReplenish.bind(this),
            afterReplenish: this.afterReplenish.bind(this),
          },
        })
        .replenish({
          destination: map(unsavedVirtualTransfers, (transfer) => ({
            fromSite: transfer.site_id,
            fromSiteName: transfer['site_name'],
            toSite: transfer.id,
            toSiteName: transfer.name,
            toSiteStoreNumber: transfer.store_number,
            inventory_items: transfer.inventory_items,
            percent: transfer.replenish_percent ?? DEFAULT_REPLENISH_PERCENT,
          })),
          strategy: btnReplenishConfig.strategy,
          messages: {
            noAvailable: this.i18nService.instant(
              I18n.ErrorMessages.BulkReplenishNoAvailableMessage,
              {
                type: REPLENISH_TYPE_NAMES.PAR,
              },
            ),
            error: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishFailedMessage,
            ),
            success: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishSuccessMessage,
              {
                replenishType: REPLENISH_TYPE_NAMES.PAR,
              },
            ),
          },
          confirmModalOptions: {
            modalTitle: btnReplenishConfig.modalTitle,
          },
        });
    };
  }

  /**
   * handle when list action
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleDestinationGridAction(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    switch (action.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.destinationGrid = grid;
        break;
      case XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED:
        const selectedTransferData = head(
          action.payload.rowData as ITransferData[],
        );

        if (
          isEmpty(get(selectedTransferData, this.formFieldPath.siteId)) ||
          isEmpty(get(selectedTransferData, this.formFieldPath.siteName))
        ) {
          // not have any form site info, just return directly
          return;
        }

        this.editSiteInventoryItems(selectedTransferData);
        break;
      default:
        return;
    }
  }

  public handleDestinationGridRemoveRow(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    const removedSiteIdToList = map(action.payload, 'site_id_to');

    if (isEmpty(removedSiteIdToList)) {
      return;
    }

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    forEach(removedSiteIdToList, (siteIdTo) =>
      unset(reallyChangedTransfers, siteIdTo),
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      { i18nService: this.i18nService },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      reallyChangedTransfers,
      false,
    );
  }

  public async printOrExport(
    data: ITransferData,
    type: COMMON_ACTION_TYPES.Print | COMMON_ACTION_TYPES.Export,
  ): Promise<void> {
    set(
      data,
      TRANSFER_FORM_FIELD_PATHS.transferGroupName,
      get(this.form.value, this.formFieldPath.name, ''),
    );
    set(
      data,
      TRANSFER_FORM_FIELD_PATHS.virtualReallyChangedTransfers,
      get(this.form.value, this.formFieldPath.virtualReallyChangedTransfers),
    );
    const isNotFinalTransfer = this.transferPdfService.isNotFinalTransfer(
      data.status,
      data.transfer_type,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];
    if (!this.isQohFromVisible) {
      modalColumns = filter(
        modalColumns,
        (column) => !isEqual(column.path, 'qoh_from'),
      );
    }
    modalColumns = addParQtyColumn(modalColumns, data.status);
    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: data.status,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });

    const totalFieldValue = !includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      data.status,
    );

    const pdfConfig = await this.transferService.getPdfConfig(
      async (definitions: IPdfDefinitions) => {
        const dmSyncResult = await this.taskService
          .getDmSyncResult([data], { isSync: true, noLoader: true })
          .toPromise();

        markTransferItemsAcknowledge(
          map(
            filter(map(dmSyncResult.sub_tasks, 'value'), 'is_acknowledge'),
            'transfer_id',
          ),
          this.form,
          this.formFieldPath,
        );

        return this.transferService.getPdfData(data, definitions?.options, {
          showQohFromOverride: this.isQohFromVisible,
          isGetUpdatedItems: false,
        });
      },
      modalColumns,
      null,
      {
        isTransferGroup: true,
        totalsConfiguration: {
          enabled: true,
          getDefaultValue: () => totalFieldValue,
        },
      },
    );
    if (type === COMMON_ACTION_TYPES.Print) {
      await this.transferGroupAutoRestockService.pdfGenerateServiceV2.printPdfAsync(
        pdfConfig,
      );
    } else {
      await this.transferGroupAutoRestockService.pdfGenerateServiceV2.downloadPdfAsync(
        pdfConfig,
      );
    }
  }

  public canPrintEmptyPdf(): boolean {
    if (!this.form) {
      return false;
    }
    const transfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return (
      isEmpty(transfers) ||
      !some(transfers, (transfer) =>
        includes(
          [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
          transfer.status,
        ),
      )
    );
  }

  public canPrintPdfOrExport(): boolean {
    if (!this.form) {
      return false;
    }
    const transfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return (
      isEmpty(transfers) ||
      every(transfers, (transfer) =>
        includes(TRANSFER_UNSAVED_STATUS, transfer.status),
      )
    );
  }

  public async refreshGrandTotal(
    param: IDocumentOperationOptions = {},
  ): Promise<void> {
    try {
      const transferGroupDetail =
        await this.transferGroupAutoRestockService.getDocumentByIdWithSkipUpdateDocument(
          this.document._id,
          merge(
            <IDocumentOperationOptions>{
              query: {
                include_transfer: true,
                transfer_fields_select:
                  this.transferGroupAutoRestockService.transferFieldsSelect.join(
                    ',',
                  ),
              },
              skipMappers: true,
            },
            param,
          ),
        );

      // need update transfer_items in document for updateGrandTotalsData calc
      this.document = merge({}, this.document, {
        virtual: {
          transfer_items: transferGroupDetail?.transfer_items ?? [],
        },
      });

      this.updateGrandTotalsData(
        getFormFieldValue(this.form, this.formFieldPath.virtualTransferItems),
      );
    } catch (e) {}
  }

  public getPrintOrExportClickHandler(
    isWrittenSheet: boolean,
    isExport: boolean,
  ): Function {
    return async (selectedItems: ITransferData[]): Promise<void> => {
      const isSingle = isEqual(size(selectedItems), 1);
      const pdfGenerateFunc = this.transferService.bulkGenerateTransferSummary(
        isWrittenSheet,
        isExport,
        {
          isTransferGroup: true,
          printAndExportMaxLength: MAX_SITES_LIMIT,
          showQohFromOverride: this.isQohFromVisible,
        },
      );

      await pdfGenerateFunc(
        this.transferGroupAutoRestockService.addAdditionalInfoToTransfer(
          selectedItems,
          getFormFieldValue(this.form, this.formFieldPath.name),
          getFormFieldValue(
            this.form,
            this.formFieldPath.virtualReallyChangedTransfers,
          ),
        ),
        this.taskService.getPdfBulkDataWithSyncLoaderFunc(
          isSingle,
          this.form,
          this.formFieldPath,
        ),
      );
    };
  }

  public getDMSyncIconRendererParams(): Record<
    string,
    string | boolean | object
  > {
    return {
      isBusy: observableOf(false),
      dataSource: observableOf([]),
      valueMapper: ({ rowData, cellValue, params }): TooltipIconConfig[] => {
        if (isNil(rowData.updated_items) && isNil(rowData.unavailable_items)) {
          return [];
        }

        return [
          {
            icon: 'icon-warning',
            class: 'xvi-grid-warn-icon',
            tooltipConfig: {
              tooltip: this.i18nService.instant(
                I18n.InfoMessages.TransferGroupDMSyncTooltipMessage,
              ),
              container: 'xvi-grid-warn-icon',
              containerClass: 'flex-container',
            },
          },
        ];
      },
    };
  }

  protected patchTransferGroupForm(storeForm: ITransfer): void {
    const reallyChangedTransfers = get(
      storeForm,
      'virtual.really_changed_transfers',
      {},
    );
    const originTransferItems = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    set(
      storeForm,
      this.formFieldPath.virtualTransferItems,
      map(
        get(storeForm, this.formFieldPath.virtualTransferItems),
        (transferItem) => {
          let newTransferItem = transferItem;

          if (!includes(TRANSFER_UNSAVED_STATUS, transferItem.status)) {
            const originTransferItem = find(originTransferItems, (item) =>
              isEqual(item._id, transferItem._id),
            );

            if (!isNil(originTransferItem)) {
              newTransferItem = originTransferItem;
            }
          }

          const targetChangedTransfer = get(
            reallyChangedTransfers,
            newTransferItem?.site_id_to,
          );

          set(
            newTransferItem,
            'isChanged',
            !isEmpty(targetChangedTransfer?.items),
          );

          return newTransferItem;
        },
      ),
    );

    this.form.patchValue(storeForm);
  }

  // eslint-disable-next-line max-lines
}
